import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft as ArrowLeftIcon,
  ShieldCheck as ShieldCheckIcon,
  AlertTriangle as ExclamationTriangleIcon,
  Loader2 as LoaderIcon,
  Search,
  ChevronDown,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  ExternalLink,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';
import ExplainButton from '../../components/ExplainButton';
import AiChatPanel from '../../components/AiChatPanel';

const FrameworkDetails = () => {
  const { scanId, frameworkId } = useParams();
  const navigate = useNavigate();
  const [frameworkData, setFrameworkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedControls, setExpandedControls] = useState({});
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);

  useEffect(() => {
    fetchFrameworkData();
  }, [scanId, frameworkId]);

  const fetchFrameworkData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(ApiNames.REPORTS_DETAILED(scanId, frameworkId));

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Framework data not found for this scan ID');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setFrameworkData(data);
    } catch (error) {
      console.error('Error fetching framework data:', error);
      setError(error.message);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate(`/Dashboard/${scanId}`);
  };

  const toggleControlExpansion = (controlId) => {
    setExpandedControls(prev => ({
      ...prev,
      [controlId]: !prev[controlId]
    }));
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pass':
      case 'compliant':
        return <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />;
      case 'fail':
      case 'non_compliant':
      case 'non compliant':
        return <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />;
      case 'partial':
      case 'partially_compliant':
      case 'partially compliant':
        return <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />;
      default:
        return <Clock className="w-5 h-5 text-cyan-600 flex-shrink-0" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'pass':
      case 'compliant':
        return <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg">Compliant</span>;
      case 'fail':
      case 'non_compliant':
      case 'non compliant':
        return <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-lg">Non Compliant</span>;
      case 'partial':
      case 'partially_compliant':
      case 'partially compliant':
        return <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-400 to-amber-500 text-white shadow-lg">Partially Compliant</span>;
      default:
        return <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-cyan-400 to-blue-500 text-white shadow-lg">Not Evaluated</span>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-lg">Critical</span>;
      case 'high':
        return <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg">High</span>;
      case 'medium':
        return <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-400 to-orange-400 text-white shadow-lg">Medium</span>;
      case 'low':
        return <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-lg">Low</span>;
      default:
        return <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-blue-400 to-indigo-500 text-white shadow-lg">Medium</span>;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressBarColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full mb-4">
            <LoaderIcon className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" />
          </div>
          <h2 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">Loading Framework Details</h2>
          <p className="text-gray-600 dark:text-gray-400">Fetching detailed control information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full mb-4">
            <ExclamationTriangleIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">Framework Error</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={handleBackToDashboard}
            className="btn-primary"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const framework = frameworkData?.framework_data;
  // Handle different data structures for different frameworks
  const controls = framework?.control_evaluations || framework?.controls || [];

  // Calculate error statistics
  const totalScanners = controls.reduce((acc, control) => {
    const scanners = control.rule_results?.length || 0;
    return acc + scanners;
  }, 0);
  const failedScanners = controls.reduce((acc, control) => {
    const failed = control.rule_results?.filter(r => r.status !== 'Compliant')?.length || 0;
    return acc + failed;
  }, 0);
  const errorControls = controls.filter(c =>
    c.status === 'FAIL' || c.status === 'Non Compliant' ||
    c.status === 'PARTIAL' || c.status === 'Partially Compliant'
  );

  // Apply filters
  let filteredControls = controls;

  // Filter by error status if toggle is on
  if (showOnlyErrors) {
    filteredControls = filteredControls.filter(control =>
      control.status === 'FAIL' || control.status === 'Non Compliant' ||
      control.status === 'PARTIAL' || control.status === 'Partially Compliant'
    );
  }

  // Filter by search term
  filteredControls = filteredControls.filter(control =>
    control.controlName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    control.controlId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    control.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getFrameworkTitle = (fwId) => {
    switch (fwId) {
      case 'USWDS':
        return 'U.S. Web Design System';
      case 'OMB-M-15-13':
        return 'OMB M-15-13 HTTPS Everywhere';
      default:
        return fwId;
    }
  };

  const getFrameworkSubtitle = (fwId) => {
    switch (fwId) {
      case 'USWDS':
        return 'Design System Standards for Federal Websites';
      case 'OMB-M-15-13':
        return 'HTTPS Security Standards for Federal Information Systems';
      default:
        return 'Federal Compliance Framework';
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleBackToDashboard}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                >
                  <ArrowLeftIcon className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Framework Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center space-x-4 mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${frameworkId === 'USWDS' ? 'bg-green-100 dark:bg-green-900' :
                frameworkId === 'OMB-M-15-13' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                <ShieldCheckIcon className={`w-6 h-6 ${frameworkId === 'USWDS' ? 'text-green-600 dark:text-green-400' :
                  frameworkId === 'OMB-M-15-13' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'
                  }`} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{getFrameworkTitle(frameworkId)}</h1>
                <p className="text-gray-600 dark:text-gray-400">{getFrameworkSubtitle(frameworkId)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl p-4 text-center shadow-md border border-blue-200 dark:border-blue-700">
                <p className="text-xs font-bold text-blue-600 dark:text-blue-300 uppercase tracking-wide mb-2">Category</p>
                <p className="text-lg font-bold text-blue-700 dark:text-blue-200">
                  {frameworkId === 'USWDS' ? 'Design System' : 'Security Framework'}
                </p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-xl p-4 text-center shadow-md border border-gray-200 dark:border-gray-600">
                <p className="text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-2">Controls Scanned</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {controls.length} of {controls.length}
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800 rounded-xl p-4 text-center shadow-md border border-purple-200 dark:border-purple-700">
                <p className="text-xs font-bold text-purple-600 dark:text-purple-300 uppercase tracking-wide mb-2">Pass Rate</p>
                <p className="text-lg font-bold text-purple-700 dark:text-purple-200">
                  {framework?.summary?.overall_score || framework?.summary?.overallScore || 0}%
                </p>
              </div>
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900 dark:to-indigo-800 rounded-xl p-4 text-center shadow-md border border-indigo-200 dark:border-indigo-700">
                <p className="text-xs font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wide mb-2">Avg Score</p>
                <p className={`text-lg font-bold ${getScoreColor(framework?.summary?.overall_score || framework?.summary?.overallScore || 0)}`}>
                  {Math.round(framework?.summary?.overall_score || framework?.summary?.overallScore || 0)}/100
                </p>
              </div>
              <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900 dark:to-red-800 rounded-xl p-4 text-center shadow-md border border-red-200 dark:border-red-700">
                <p className="text-xs font-bold text-red-600 dark:text-red-300 uppercase tracking-wide mb-2">Critical Issues</p>
                <p className="text-lg font-bold text-red-700 dark:text-red-300">
                  {controls.filter(c => c.priority === 'Critical' || c.priority === 'High').length}
                </p>
              </div>
            </div>

            {/* Status Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 text-center shadow-lg border-2 border-green-200 dark:border-green-700 hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full mb-3">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400 mb-1">
                  {framework?.summary?.compliant || controls.filter(c => c.status === 'PASS' || c.status === 'Compliant').length}
                </p>
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">Compliant</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 text-center shadow-lg border-2 border-red-200 dark:border-red-700 hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-full mb-3">
                  <XCircle className="w-6 h-6 text-white" />
                </div>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400 mb-1">
                  {framework?.summary?.nonCompliant || controls.filter(c => c.status === 'FAIL' || c.status === 'Non Compliant').length}
                </p>
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">Non-Compliant</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 text-center shadow-lg border-2 border-yellow-200 dark:border-yellow-700 hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full mb-3">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mb-1">
                  {framework?.summary?.partiallyCompliant || controls.filter(c => c.status === 'PARTIAL' || c.status === 'Partially Compliant').length}
                </p>
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">Partially Compliant</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 text-center shadow-lg border-2 border-gray-200 dark:border-gray-600 hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full mb-3">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <p className="text-3xl font-bold text-gray-600 dark:text-gray-300 mb-1">{framework?.summary?.notEvaluated || 0}</p>
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">Not Evaluated</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search and Filter */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1 max-w-xl">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search controls by name, ID, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm text-sm font-medium transition-all duration-200"
                />
              </div>
              <button
                onClick={() => setShowOnlyErrors(!showOnlyErrors)}
                className={`inline-flex items-center px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-200 shadow-md whitespace-nowrap ${showOnlyErrors
                  ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
              >
                <AlertTriangle className="w-5 h-5 mr-2" />
                {showOnlyErrors ? `Showing Non-Compliant Only (${errorControls.length})` : 'Show Only Non-Compliant'}
              </button>
            </div>
          </div>

          {/* Controls Header */}
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/40 dark:to-indigo-900/40 rounded-xl p-6 border-l-4 border-blue-500 shadow-md">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-4 shadow-md">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Control Details</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">{filteredControls.length} control{filteredControls.length !== 1 ? 's' : ''} found</p>
              </div>
            </div>
          </div>

          {/* Controls Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="w-full" style={{ overflow: "auto" }}>
              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-750">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider" style={{ width: '160px' }}>
                      Scanner
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider" style={{ width: '200px' }}>
                      Control
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider" style={{ width: '320px' }}>
                      Description
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider" style={{ width: '190px' }}>
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider" style={{ width: '130px' }}>
                      Priority
                    </th>

                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider" style={{ width: '140px' }}>
                      Score
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider" style={{ width: '150px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredControls.map((control) => {
                    return (
                      <React.Fragment key={control.controlId}>
                        <tr className="hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-150 border-l-4 border-transparent hover:border-blue-500">
                          <td className="px-6 py-5">
                            <span className="inline-block px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 text-blue-800 dark:text-blue-200 shadow-sm break-words max-w-full">
                              {control.scanner || 'UNKNOWN'}
                            </span>
                          </td>
                          <td className="px-6 py-5">
                            <div>
                              <div className="text-sm font-bold text-gray-900 dark:text-gray-100 break-words">{control.controlId}</div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 font-medium break-words leading-relaxed">{control.controlName}</div>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed break-words">
                              {control.description && (
                                <div className="mb-2 p-3 bg-blue-50 dark:bg-gray-700 border-l-4 border-blue-500 rounded-r">
                                  <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide">Description:</span>
                                  <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100 break-words leading-relaxed">{control.description}</p>
                                </div>
                              )}
                              {!control.description && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 italic">No description available</p>
                              )}
                              {control.finding && (
                                <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/30 border-l-2 border-orange-400 rounded">
                                  <span className="text-xs font-bold text-orange-700 dark:text-orange-300">Finding:</span>
                                  <span className="text-xs text-orange-600 dark:text-orange-300 ml-1 break-words">{control.finding}</span>
                                </div>
                              )}
                              {control.evidence && (
                                <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/30 border-l-2 border-blue-400 rounded">
                                  <span className="text-xs font-bold text-blue-700 dark:text-blue-300">Evidence:</span>
                                  <span className="text-xs text-blue-600 dark:text-blue-300 ml-1 break-words">{control.evidence}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(control.status)}
                              {getStatusBadge(control.status)}
                            </div>
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap">
                            {getPriorityBadge(control.priority)}
                          </td>

                          <td className="px-6 py-5 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-3 w-16 shadow-inner min-w-[64px]">
                                <div
                                  className={`h-3 rounded-full ${getProgressBarColor(control.score)} shadow-sm transition-all duration-300`}
                                  style={{ width: `${control.score}%` }}
                                ></div>
                              </div>
                              <span className={`text-sm font-bold ${getScoreColor(control.score)} whitespace-nowrap`}>
                                {control.score}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-5 whitespace-nowrap">
                            <button
                              onClick={() => toggleControlExpansion(control.controlId)}
                              className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg font-semibold text-xs"
                            >
                              <ChevronDown
                                className={`w-4 h-4 mr-1 transform transition-transform duration-200 ${expandedControls[control.controlId] ? 'rotate-180' : ''
                                  }`}
                              />
                              {expandedControls[control.controlId] ? 'Hide' : 'View'}
                            </button>
                          </td>
                        </tr>
                        {expandedControls[control.controlId] && (
                          <tr>
                            <td colSpan="7" className="px-8 py-6 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20">
                              <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow duration-200">
                                    <div className="flex items-center mb-4">
                                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                                        <FileText className="w-5 h-5 text-white" />
                                      </div>
                                      <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100">Remediation</h4>
                                    </div>
                                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4" style={{ whiteSpace: "break-spaces" }}>
                                      {control.actions?.remediation || 'Implement Content-Security-Policy header with appropriate directives to prevent XSS and data injection attacks'}
                                    </p>
                                    <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                                      <div className="flex items-center">
                                        <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                                        <span className="text-xs font-bold text-gray-700 dark:text-gray-300">Timeline:</span>
                                        <span className="text-xs text-gray-600 dark:text-gray-400 ml-2 px-2 py-1 bg-blue-100 dark:bg-blue-900/50 rounded">
                                          {control.actions?.timeline || 'Immediate (0-7 days)'}
                                        </span>
                                      </div>
                                      <div className="flex items-center">
                                        <AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-400 mr-2" />
                                        <span className="text-xs font-bold text-gray-700 dark:text-gray-300">CWE:</span>
                                        <span className="text-xs text-gray-600 dark:text-gray-400 ml-2 px-2 py-1 bg-orange-100 dark:bg-orange-900/50 rounded">
                                          {control.actions?.cwe || 'CWE-693'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow duration-200">
                                    <div className="flex items-center mb-4">
                                      <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center mr-3">
                                        <ExternalLink className="w-5 h-5 text-white" />
                                      </div>
                                      <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100">References</h4>
                                    </div>
                                    <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
                                      {control.actions?.references?.map((ref, index) => {
                                        // Handle different reference formats
                                        let url, title;
                                        if (typeof ref === 'string') {
                                          // String format - check if it's a URL
                                          if (ref.startsWith('http://') || ref.startsWith('https://')) {
                                            url = ref;
                                            title = ref;
                                          } else {
                                            // Plain text reference
                                            title = ref;
                                          }
                                        } else if (typeof ref === 'object' && ref !== null) {
                                          // Object format with url and title properties
                                          url = ref.url || ref.link || ref.href;
                                          title = ref.title || ref.name || ref.description || url;
                                        }

                                        return (
                                          <li key={index} className="flex items-start p-2 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                                            <ExternalLink className="w-4 h-4 mr-3 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                                            {url ? (
                                              <a
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline break-all cursor-pointer"
                                                onClick={(e) => {
                                                  console.log('Reference link clicked:', url);
                                                }}
                                              >
                                                {title}
                                              </a>
                                            ) : (
                                              <span className="break-words dark:text-gray-300">{title || 'No reference available'}</span>
                                            )}
                                          </li>
                                        );
                                      }) || (
                                          <>
                                            <li className="flex items-start p-2 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                                              <ExternalLink className="w-4 h-4 mr-3 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                                              <span className="dark:text-gray-300">{frameworkId === 'USWDS' ? 'USWDS Compliance Documentation' : 'OMB M-15-13 HTTPS Everywhere Documentation'}</span>
                                            </li>
                                            <li className="flex items-start p-2 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                                              <ExternalLink className="w-4 h-4 mr-3 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                                              <span className="dark:text-gray-300">OWASP {frameworkId === 'USWDS' ? 'Design System' : 'HTTPS'} Prevention</span>
                                            </li>
                                          </>
                                        )}
                                    </ul>
                                  </div>
                                </div>

                                {/* Detailed Scanner Error Results */}
                                {control.rule_results && control.rule_results.length > 0 && (
                                  <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                                    <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-gray-800 dark:to-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                                      <div className="flex items-center">
                                        <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 mr-3" />
                                        <h4 className="text-lg font-bold text-gray-900 dark:text-white">Scanner Error Details</h4>
                                        <span className="ml-auto text-sm font-semibold text-gray-600 dark:text-gray-300">
                                          {control.rule_results.length} scanner{control.rule_results.length !== 1 ? 's' : ''} evaluated
                                        </span>
                                      </div>
                                    </div>

                                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                      {control.rule_results.map((ruleResult, ruleIndex) => (
                                        <div key={ruleIndex} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                          {/* Scanner Header */}
                                          <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center space-x-3">
                                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ruleResult.status === 'Compliant' ? 'bg-green-100 dark:bg-green-900/50' :
                                                ruleResult.status === 'Partially Compliant' ? 'bg-yellow-100 dark:bg-yellow-900/50' :
                                                  'bg-red-100 dark:bg-red-900/50'
                                                }`}>
                                                {ruleResult.status === 'Compliant' ? (
                                                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                                ) : ruleResult.status === 'Partially Compliant' ? (
                                                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                                                ) : (
                                                  <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                                )}
                                              </div>
                                              <div>
                                                <h5 className="font-bold text-gray-900 dark:text-gray-100 uppercase text-sm tracking-wide">
                                                  {ruleResult.scanner} Scanner
                                                </h5>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Rule ID: {ruleResult.rule_id}</p>
                                              </div>
                                            </div>
                                            <div className="text-right">
                                              <div className={`text-2xl font-bold ${ruleResult.score >= 90 ? 'text-green-600 dark:text-green-400' :
                                                ruleResult.score >= 70 ? 'text-yellow-600 dark:text-yellow-400' :
                                                  'text-red-600 dark:text-red-400'
                                                }`}>
                                                {ruleResult.score}%
                                              </div>
                                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{ruleResult.status}</div>
                                            </div>
                                          </div>

                                          {/* Scanner Finding & Evidence */}
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            <div className="bg-orange-50 dark:bg-gray-700 rounded-lg p-4 border-l-4 border-orange-500">
                                              <p className="text-xs font-bold text-orange-700 dark:text-orange-300 uppercase tracking-wide mb-2">Finding</p>
                                              <p className="text-sm text-gray-800 dark:text-gray-100 font-medium">{ruleResult.finding}</p>
                                            </div>
                                            <div className="bg-blue-50 dark:bg-gray-700 rounded-lg p-4 border-l-4 border-blue-500">
                                              <p className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-2">Evidence</p>
                                              <p className="text-sm text-gray-800 dark:text-gray-100 font-medium">{ruleResult.evidence}</p>
                                            </div>
                                          </div>

                                          {/* Condition-Level Errors */}
                                          {ruleResult.condition_results && ruleResult.condition_results.length > 0 && (
                                            <div className="mt-4">
                                              <h6 className="text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider mb-3 flex items-center">
                                                <FileText className="w-4 h-4 mr-2" />
                                                Detailed Condition Checks ({ruleResult.condition_results.length})
                                              </h6>
                                              <div className="space-y-2">
                                                {ruleResult.condition_results.map((condition, condIndex) => (
                                                  <div
                                                    key={condIndex}
                                                    className={`flex items-start p-3 rounded-lg border ${condition.passed
                                                      ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-600'
                                                      : 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-600'
                                                      }`}
                                                  >
                                                    <div className="flex-shrink-0 mt-0.5">
                                                      {condition.passed ? (
                                                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                                      ) : (
                                                        <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                                      )}
                                                    </div>
                                                    <div className="ml-3 flex-1">
                                                      <div className="flex items-center justify-between mb-2">
                                                        <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                                          {condition.field}
                                                        </span>
                                                        <span className={`text-xs font-bold px-2 py-1 rounded ${condition.passed
                                                          ? 'bg-green-500 text-white'
                                                          : 'bg-red-500 text-white'
                                                          }`}>
                                                          {condition.passed ? 'PASS' : 'FAIL'}
                                                        </span>
                                                      </div>
                                                      <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1.5">
                                                        <div className="flex items-center gap-2">
                                                          <span className="font-semibold text-gray-700 dark:text-gray-200 min-w-[60px]">Operator:</span>
                                                          <code className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-2 py-0.5 rounded">{condition.operator}</code>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                          <span className="font-semibold text-gray-700 dark:text-gray-200 min-w-[60px]">Expected:</span>
                                                          <code className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-0.5 rounded">
                                                            {JSON.stringify(condition.expected)}
                                                          </code>
                                                        </div>
                                                        <div className="flex items-start gap-2">
                                                          <span className="font-semibold text-gray-700 dark:text-gray-200 min-w-[60px]">Evidence:</span>
                                                          <span className={condition.passed ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                                                            {condition.evidence}
                                                          </span>
                                                        </div>
                                                        {condition.weight && (
                                                          <div className="flex items-center gap-2">
                                                            <span className="font-semibold text-gray-700 dark:text-gray-200 min-w-[60px]">Weight:</span>
                                                            <span className="text-gray-700 dark:text-gray-200">{condition.weight}</span>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* AI-Powered Explanation for Failed Controls */}
                                {(control.status?.toLowerCase() === 'fail' ||
                                  control.status?.toLowerCase() === 'non_compliant' ||
                                  control.status?.toLowerCase() === 'non compliant' ||
                                  control.status?.toLowerCase() === 'partial' ||
                                  control.status?.toLowerCase() === 'partially_compliant' ||
                                  control.status?.toLowerCase() === 'partially compliant') && (
                                    <ExplainButton
                                      control={{
                                        controlId: control.controlId,
                                        controlName: control.controlName,
                                        frameworkName: getFrameworkTitle(frameworkId),
                                        finding: control.finding || control.description || 'Control failed compliance check',
                                        priority: control.priority || 'Unknown'
                                      }}
                                    />
                                  )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* AI Chat Panel - Rendered outside main div for fixed positioning */}
      {frameworkData.framework_data && (
        <>
          {console.log('🎨 Rendering AiChatPanel with scanId:', scanId, 'url_name:', frameworkData.url_name)}
          <AiChatPanel
            scanId={scanId}
            url_name={frameworkData.url_name || 'Compliance Scan'}
          />
        </>
      )}
    </>
  );
};

export default FrameworkDetails;
